import { Injectable } from '@angular/core';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

@Injectable({
  providedIn: 'root'
})
export class AnimationService {
  /**
   * Elements already claimed by a scroll-reveal tween. Reveal animations set
   * `opacity: 0` up front (`gsap.from` + immediateRender), so if two of them
   * target the same element (e.g. a `.reveal` card that also matches a broad
   * `[class*="card"]` selector), the second tween captures the first's
   * already-zeroed opacity as its *end* value and pins the element invisible
   * forever. We guard against that: the first reveal to claim an element wins,
   * every later one skips it.
   */
  private readonly claimed = new WeakSet<HTMLElement>();

  /** Return only the not-yet-claimed elements for a selector, marking them. */
  private claim(selector: string): HTMLElement[] {
    return gsap.utils.toArray<HTMLElement>(selector).filter((el) => {
      if (this.claimed.has(el)) return false;
      this.claimed.add(el);
      return true;
    });
  }

  /**
   * Fade in elements on scroll with stagger effect (3-5 seconds per element).
   * @param selector - CSS selector for elements to animate
   * @param stagger - Delay between each element animation (default: 0.4s)
   */
  fadeInOnScroll(selector: string, stagger = 0.4) {
    this.claim(selector).forEach((el, index) => {
      gsap.from(el, {
        opacity: 0,
        y: 60,
        duration: 1.5,
        delay: Math.min(index * stagger, 0.4),
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
        ease: 'power2.out',
      });
    });
  }

  /**
   * Staggered slide in from left on scroll (slow, 3-5 seconds).
   */
  slideInLeft(selector: string, stagger = 0.5) {
    this.claim(selector).forEach((el, index) => {
      gsap.from(el, {
        opacity: 0,
        x: -100,
        duration: 1.5,
        delay: Math.min(index * stagger, 0.4),
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
        ease: 'power2.out',
      });
    });
  }

  /**
   * Staggered slide in from right on scroll (slow, 3-5 seconds).
   */
  slideInRight(selector: string, stagger = 0.5) {
    this.claim(selector).forEach((el, index) => {
      gsap.from(el, {
        opacity: 0,
        x: 100,
        duration: 1.5,
        delay: Math.min(index * stagger, 0.4),
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
        ease: 'power2.out',
      });
    });
  }

  /**
   * Staggered slide in from top on scroll.
   */
  slideInUp(selector: string, stagger = 0.5) {
    this.claim(selector).forEach((el, index) => {
      gsap.from(el, {
        opacity: 0,
        y: -100,
        duration: 1.5,
        delay: Math.min(index * stagger, 0.4),
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
        ease: 'power2.out',
      });
    });
  }

  /**
   * Staggered slide in from bottom on scroll.
   */
  slideInDown(selector: string, stagger = 0.5) {
    this.claim(selector).forEach((el, index) => {
      gsap.from(el, {
        opacity: 0,
        y: 100,
        duration: 1.5,
        delay: Math.min(index * stagger, 0.4),
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
        ease: 'power2.out',
      });
    });
  }

  /**
   * Scale up animation on scroll (slow, 3-5 seconds).
   */
  scaleUp(selector: string, stagger = 0.5) {
    this.claim(selector).forEach((el, index) => {
      gsap.from(el, {
        opacity: 0,
        scale: 0.7,
        duration: 1.5,
        delay: Math.min(index * stagger, 0.4),
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
        ease: 'power2.out',
      });
    });
  }

  /**
   * Hover scale effect.
   */
  hoverScale(selector: string, scale = 1.05) {
    gsap.utils.toArray<HTMLElement>(selector).forEach((el) => {
      el.addEventListener('mouseenter', () => {
        gsap.to(el, { scale, duration: 0.3, overwrite: 'auto' });
      });
      el.addEventListener('mouseleave', () => {
        gsap.to(el, { scale: 1, duration: 0.3, overwrite: 'auto' });
      });
    });
  }

  /**
   * Parallax effect on scroll.
   */
  parallax(selector: string, speed = 0.5) {
    gsap.utils.toArray<HTMLElement>(selector).forEach((el) => {
      gsap.to(el, {
        y: () => window.innerHeight * speed,
        scrollTrigger: {
          trigger: el,
          scrub: true,
          start: 'top center',
        },
      });
    });
  }

  /**
   * Staggered animation for a group of elements.
   */
  staggerIn(selector: string, duration = 0.6, stagger = 0.15) {
    gsap.to(selector, {
      opacity: 1,
      y: 0,
      duration,
      stagger,
      ease: 'power2.out',
    });
  }

  /**
   * Count animation (useful for stats/numbers).
   */
  countUp(selector: string, duration = 2) {
    gsap.utils.toArray<HTMLElement>(selector).forEach((el) => {
      const finalValue = parseFloat(el.textContent || '0');
      if (isNaN(finalValue)) return;

      gsap.fromTo(
        el,
        { textContent: 0 },
        {
          textContent: finalValue,
          duration,
          snap: { textContent: 1 },
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
          ease: 'power1.out',
        }
      );
    });
  }

  /**
   * Sticky "stacking cards" effect. The cards are pinned near the top of the
   * viewport via CSS `position: sticky` (set in the stylesheet), each at a
   * slightly lower offset so earlier cards peek out behind later ones. This
   * method adds the perspective "recede": as the next card scrolls up to cover
   * the current one, the current card scales down and dims, so the deck looks
   * layered in 3D. The last card never recedes — it's the one left on top.
   *
   * Respects prefers-reduced-motion: the sticky stack still works (it's just
   * layout), but the scrub-driven scale/dim is skipped.
   */
  stackCards(selector: string) {
    const cards = gsap.utils.toArray<HTMLElement>(selector);
    if (cards.length < 2) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    cards.forEach((card, i) => {
      card.style.transformOrigin = 'center top';
      if (reduce || i === cards.length - 1) return;

      const next = cards[i + 1];
      gsap.fromTo(
        card,
        { scale: 1, filter: 'brightness(1)' },
        {
          scale: 0.9,
          filter: 'brightness(0.7)',
          ease: 'none',
          scrollTrigger: {
            // Driven by the NEXT card's travel: begins as it enters from the
            // bottom, completes once it has scrolled up over this card.
            trigger: next,
            start: 'top 92%',
            end: 'top 22%',
            scrub: true,
          },
        }
      );
    });

    ScrollTrigger.refresh();
  }

  /**
   * Recomputes ScrollTrigger start/end positions whenever a still-loading image
   * finishes. Large lazy-loaded images below the fold change the page height as
   * they arrive; without this, `reveal` elements beneath them fire at stale
   * positions and appear to "show up" much later than they should.
   */
  refreshAfterImages() {
    const pending = Array.from(document.images).filter(img => !img.complete);
    if (!pending.length) return;
    pending.forEach(img => {
      const refresh = () => ScrollTrigger.refresh();
      img.addEventListener('load', refresh, { once: true });
      img.addEventListener('error', refresh, { once: true });
    });
  }
}